import {FileProxy} from 'substance'
import {FileService} from './FileService'

class DWFileProxy extends FileProxy {

    constructor(fileNode, context) {
        super(fileNode, context)

        this.context = context

        // 1. A file is considered upstream if it has a uuid set
        // 2. If no uuid but a local file is present, the file needs to be sync'd
        // 3. Due to permission reasons 'url' needs to be retrieved from the server everytime
        //    thus it is a volatile property

        this.fileService = new FileService(this.context.api)
        this.fileNode = fileNode
        // used locally e.g. after drop or file dialog

        // If a file upload is in progress
        this.uploadPromise = null

        // If the sync promise ever got rejected
        this.rejected = false

        this.hasLoadingErrors = false

        // When an url (String) is given as the data an uri needs to be 'uploaded'
        if (fileNode.sourceUrl) {
            this.sourceUrl = fileNode.sourceUrl
        } else {
            this.sourceFile = fileNode.sourceFile
            if (this.sourceFile) {
                this._fileUrl = URL.createObjectURL(this.sourceFile)
            }
        }

        // TODO: this should be consistent: either always fetch the url or never
        this.url = fileNode.url
        if (!this.url && fileNode.uuid) {
            this.fetchUrl()
        }
    }

    /**
     *
     * @throws Error - When there is an error
     * @returns {*}
     */
    getUrl() {
        // if we have fetched the url already, just serve it here
        if (this.url) {
            return this.url
        }
        if (this.sourceUrl) {
            return this.sourceUrl
        }
        // if we have a local file, use it's data URL
        if (this._fileUrl) {
            return this._fileUrl
        }
        if (this.hasLoadingErrors) {
            throw new Error('Error fetching url for UUID ' + this.fileNode.uuid)
        }
        // no URL available
        return ''
    }

    fetchUrl() {
        this.fileService.getUrl(this.fileNode.uuid, this.fileNode.imType, this.fileNode.uri)
            .then((url) => {
                this.url = url
                this.triggerUpdate()
            })
            .catch(() => {
                this.hasLoadingErrors = true
                this.triggerUpdate()
            })
    }

    /**
     * URLs for specific rendering options, 'fallbacks' may be specified here
     * @param fallbacks The interesting fallback URLs
     * @param params Optional rendering parameters, for use with service URL
     * @return {Promise.<TResult>}
     */
    fetchSpecifiedUrls(fallbacks, params) {
        return this.fileService.getUrl(this.fileNode.uuid, this.fileNode.imType, this.fileNode.uri, fallbacks, params)
            .then((url) => {
                return url
            })
            .catch(e => {
                return Promise.reject(e)
            })
    }

    sync() {
        if (this.uploadPromise) {
            return this.uploadPromise
        }

        const api = this.context.api
        // TODO If node does not exist in document, skip sync.
        // TODO Remove this when proxy node is being properly removed when image node is deleted
        const nodeInDocument = api.editorSession.getDocument().get(this.fileNode.id)

        if (!nodeInDocument) {
            return Promise.resolve(this.fileNode.uuid)
        }

        if (!this.fileNode.uuid && this.sourceFile) { // regular file upload
            this.uploadPromise = new Promise((resolve, reject) => {
                const {imType} = this.fileNode

                if (!imType) {
                    this.rejected = true
                    reject(new Error('Trying to upload a file without ImType'))
                }

                this.fileService.uploadFile(this.sourceFile, {imType})
                    .then((xmlString) => {
                        return this.fileNode.handleDocument(xmlString, this.context)
                    })
                    .then(() => {
                        if (!this.fileNode.uuid) {
                            this.rejected = true
                            reject(new Error('Uploaded file has no uuid, upload aborted'))
                        }
                        else {
                            this.fileNode.sourceFile = null // Remove sourceFile from filenode to force use of Imgix instead of Sourcefile
                            this.uploadPromise = null
                            this.fetchUrl()
                            resolve(this.fileNode.uuid)
                        }
                    })
                    .catch((e) => {
                        if (api.editorSession.document.get(this.fileNode.parentNodeId)) {
                            if (e.name === 'FileUploadError' && e.message.indexOf('Error executing lambda for creating image version') !== -1) {
                                // TODO: Improve error from server and remove this suppression
                                // Suppress notification if the error is caused by an unsupported image. The ximimage plugin handles the error
                            } else {
                                api.ui.showNotification('DWFile Upload', api.getLabel('Error occured'), api.getLabel('An error occured') + ': ' + e.message)
                            }
                        }

                        this.rejected = true
                        this.uploadPromise = null
                        reject(e)
                    })
            })
        }
        else if (!this.fileNode.uuid && this.sourceUrl) { // uri-based upload
            this.uploadPromise = new Promise((resolve, reject) => {
                if (!this.fileNode.imType) {
                    this.rejected = true
                    reject(new Error('Trying to upload a file without ImType'))
                }
                this.fileService.uploadURL(this.sourceUrl, this.fileNode.imType)
                    .then((xmlString) => {
                        this.fileNode.handleDocument(xmlString, this.context).then(() => {
                            this.uploadPromise = null
                            this.fetchUrl()
                            resolve(this.fileNode.uuid)
                        })
                    })
                    .catch((e) => {
                        api.ui.showNotification('DWFile Upload', api.getLabel('Error occured'), api.getLabel('An error occured') + ': ' + e.message)
                        this.uploadPromise = null
                        this.rejected = true
                        reject(e)
                    })
            })


        } else {
            return Promise.resolve(this.fileNode.uuid)
        }

        return this.uploadPromise
    }


}

export {DWFileProxy}
